import React, { useState } from "react";
import { useApp } from "../context/AppContext";
import { useFetch, NeedTenant } from "../hooks/useData";
import { GlassCard, Loading, EmptyState, SectionTitle, fmtHours } from "../components/common";
import { Button } from "../components/ui/button";
import { Slider } from "../components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Legend } from "recharts";
import client from "../lib/api";
import { FlaskConical, Play, Zap, TrendingDown, Loader2 } from "lucide-react";
import { toast } from "sonner";

function Inner() {
  const { datasetId } = useApp();
  const { data: perf, loading } = useFetch(datasetId ? `/datasets/${datasetId}/performance` : null, { skip: !datasetId });
  const [automate, setAutomate] = useState("none");
  const [remove, setRemove] = useState("none");
  const [resMult, setResMult] = useState([1]);
  const [volMult, setVolMult] = useState([1]);
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);

  if (!datasetId) return <EmptyState icon={FlaskConical} title="No dataset selected" />;
  if (loading) return <Loading />;
  if (!perf) return null;

  const activities = perf.activity_performance.map((a) => a.activity);

  const run = async () => {
    setRunning(true);
    try {
      const { data } = await client.post("/simulate", {
        dataset_id: datasetId,
        automate_activity: automate === "none" ? null : automate,
        remove_activity: remove === "none" ? null : remove,
        resource_multiplier: resMult[0],
        volume_multiplier: volMult[0],
      });
      setResult(data);
      toast.success("Simulation complete");
    } catch (e) { toast.error("Simulation failed"); }
    finally { setRunning(false); }
  };

  const chartData = result ? [
    { name: "Avg Cycle (h)", Baseline: result.baseline.avg_duration_hours, Simulated: result.simulated.avg_duration_hours },
    { name: "Cases", Baseline: result.baseline.cases, Simulated: result.simulated.cases },
  ] : [];

  return (
    <div className="space-y-5 pm-fade-up">
      <SectionTitle eyebrow="What-If Modelling" title="Simulation Sandbox" sub="Model process changes and predict outcomes before you commit" />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <GlassCard className="lg:col-span-5 p-6 space-y-5">
          <h3 className="font-bold text-slate-900 flex items-center gap-2"><Zap className="w-4 h-4 text-cyan-600" />Scenario builder</h3>
          <div>
            <label className="text-sm font-medium text-slate-600 mb-1.5 block">Automate activity</label>
            <Select value={automate} onValueChange={setAutomate}>
              <SelectTrigger className="bg-white/70 border-white/60 rounded-xl" data-testid="sim-automate"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="none">None</SelectItem>{activities.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-600 mb-1.5 block">Eliminate activity</label>
            <Select value={remove} onValueChange={setRemove}>
              <SelectTrigger className="bg-white/70 border-white/60 rounded-xl" data-testid="sim-remove"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="none">None</SelectItem>{activities.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-600 mb-2 flex justify-between"><span>Resource capacity</span><span className="text-cyan-700 font-bold">{resMult[0].toFixed(1)}×</span></label>
            <Slider value={resMult} onValueChange={setResMult} min={0.5} max={3} step={0.1} data-testid="sim-resource" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-600 mb-2 flex justify-between"><span>Case volume</span><span className="text-cyan-700 font-bold">{volMult[0].toFixed(1)}×</span></label>
            <Slider value={volMult} onValueChange={setVolMult} min={0.5} max={5} step={0.1} data-testid="sim-volume" />
          </div>
          <Button onClick={run} disabled={running} data-testid="run-simulation" className="w-full h-11 rounded-xl pm-gradient text-white border-0 pm-transition hover:opacity-95">
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Play className="w-4 h-4 mr-2" />Run simulation</>}
          </Button>
        </GlassCard>

        <GlassCard className="lg:col-span-7 p-6">
          <h3 className="font-bold text-slate-900 mb-4">Predicted outcome</h3>
          {!result ? (
            <div className="h-72 flex items-center justify-center text-slate-400 text-sm">Configure a scenario and run the simulation to see projected impact.</div>
          ) : (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200">
                  <div className="flex items-center gap-1.5 text-emerald-700 text-sm font-semibold"><TrendingDown className="w-4 h-4" />Cycle time reduction</div>
                  <div className="text-3xl font-bold text-emerald-700 font-display mt-1">{result.duration_reduction_percent}%</div>
                  <div className="text-xs text-emerald-600/80 mt-1">{fmtHours(result.baseline.avg_duration_hours)} → {fmtHours(result.simulated.avg_duration_hours)}</div>
                </div>
                <div className="p-4 rounded-xl bg-cyan-50 border border-cyan-200">
                  <div className="flex items-center gap-1.5 text-cyan-700 text-sm font-semibold"><Zap className="w-4 h-4" />Throughput uplift</div>
                  <div className="text-3xl font-bold text-cyan-700 font-display mt-1">{result.throughput_uplift_percent}%</div>
                  <div className="text-xs text-cyan-600/80 mt-1">{result.baseline.cases} → {result.simulated.cases} cases</div>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData} margin={{ left: -20 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }} />
                  <Legend />
                  <Bar dataKey="Baseline" fill="#cbd5e1" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="Simulated" fill="#06b6d4" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </GlassCard>
      </div>
    </div>
  );
}

export default function Simulation() { return <NeedTenant><Inner /></NeedTenant>; }
